<!-- ValuesEditor.vue -->
<template>
  <div>
    <div v-if="!loaded">
      Loading Values sets...
    </div>
    <div v-if="loaded">
      <p class="title has-text-centered">Your Values Sets</p>
      
      <!-- Add new set button -->
      <div class="buttons is-centered mb-6">
        <button class="button is-rounded is-medium is-primary" @click="toggleScreensList">
          <span>{{ creatingNew ? 'Hide' : 'Create new set of values' }}</span>
          <span class="icon">
            <i :class="creatingNew ? 'fas fa-eye-slash' : 'fas fa-plus'"></i>
          </span>
        </button>
      </div>

      <div class="card-content">
        <div v-show="creatingNew">
          <p class="subtitle is-6 has-text-centered has-text-grey ">
            <em>Select metrics to include in your set of Values, then click save to make them available elsewhere.</em>
          </p>
          <br>
          <div class="columns">
            <div class="column is-four-fifths">
              <ScreensList
                v-bind:screens="metrics"
                v-bind:companies="{a:[], byId: {}}"
                mode="selecting"
                v-on:set-update="setUpdated" ref="screensList"
              >
                Edit Values Set
              </ScreensList>
            </div>
            <div class="column is-one-fifth box has-text-centered">
                {{ activeValue.metrics.length }} metrics in this set
              <div class="tags">
                <div
                  v-for="metric in activeValue.metrics"
                  v-if="metric.details"
                  v-bind:class="['tag', 'is-light', 'is-'+metric.details.subcategory[0].esgissue.slug]"
                >
                  {{ metric.details.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div class="field has-addons has-addons-centered mb-4">
        <div class="control has-icons-left is-expanded" style="max-width: 500px;">
          <input 
            class="input" 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search for values sets..."
          >
          <span class="icon is-left">
            <i class="fas fa-search"></i>
          </span>
        </div>
      </div>

      <!-- Values sets grid -->
      <!-- Comment from Github for how we should not copy this pattern: The way we are handling "Is this user allowed to edit this screenset?" seems a bit weird to me. I'm guessing it's an artifact of older code and minimizing changes, but if we are going to use this as a template for managing perms in general, it might be worth refactoring. The backend should probably inject the permissions (can_change, etc) into each object so the frontend doesn't have to manage it. -->
      <div class="values-sets-container">
        <ValueSetCard
          v-for="set in filteredSets"
          :key="set.id"
          :valueSet="set"
          :editableSets="editableSets"
          @edit="activate"
          @delete="deleteSet"
        />
      </div>
    </div><!-- card -->
    <ScreenDetailPane />
  </div>

</template>

<script>
  import ValueSetCard from '@frontend/dash3/js/components/ValueSetCard.vue';
  import Advisor from '@frontend/dash3/js/shared/Advisor.js';

  import ScreensList from '@frontend/builder/js/components/ScreensList.vue';
  import ScreenDetailPane from '@frontend/builder/js/components/ScreenDetailPane.vue';
  import ValueTag from '@frontend/dash3/js/components/ValueTag.vue';
  import _ from 'lodash';

  export default {
    data() {
      return {
        metrics: {
          a: [],
          byId: {},
        },
        values: [],
        activeValue: {
          metrics: [],
        },
        creatingNew: false,
        editableSets: new Set(),

        loaded: false,
        showDeleteButton: true,
        searchQuery: '',
        searchFilter: 'all',
      }
    },
    components: {
      ScreensList, ScreenDetailPane, ValueTag, ValueSetCard
    },
    methods: {
      activate: function(set) {        
        this.activeValue = set;
        console.log('SET IS HERE')
        console.log(set)
        this.$nextTick(() => {
          this.$root.$emit('loaded-screenset-edit', this.activeValue);
          this.$refs.screensList.applyFilter('applied');
        })
        this.activeValue.checkedRows = this.metrics.a.filter((itm, idx, arr) => {
          return this.activeValue.metric_ids.indexOf(itm.id) > -1;
        });
        this.creatingNew = true;
      },
      cancelEdit: function(evt) {
        this.activeValue = {metrics: [],};
        this.creatingNew = false;
      },
      newSet: function(evt) {
        let newItem = {
          name: 'Untitled'
        }
        this.values.unshift(newItem);
        this.activeValue = newItem;
      },
      toggleScreensList: function (evt) {
        if (this.creatingNew) {
          this.creatingNew = false;
          this.activeValue = {metrics: [],};
        } else {
          const blankSet = {
            metrics: [],
            name: 'Untitled',
          };
          this.creatingNew = true;
          this.activeValue = blankSet;
          this.$root.$emit('loaded-screenset', blankSet);
          this.$nextTick(() => {
            this.$refs.screensList.applyFilter('recommended');
          });
        }
      },
      setUpdated: function(newMetrics) {
        if (newMetrics.length > 0) {
          this.activeValue.metrics = [];
          this.activeValue.metrics.push(...newMetrics);
        }
      },
      doSave: function() {
        let method, url;
        if (this.activeValue.id) {
          method = 'PUT';
          url = `/api/v1/screen_sets/${this.activeValue.id}/`;
        }
        else {
          method = 'POST';
          url = `/api/v1/screen_sets/`;
        }

        fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            'name': this.activeValue.name,
            'metric_thresholds': this.activeValue.checkedRows.map((itm) => {
              return {
                'metric_description_id': itm.id,
                'threshold': null,
              }
            })
          }),
        }).then((response) => {
          if (response.ok) {
            response.json().then((data) => {
              if (!this.activeValue.id) {
                this.activeValue.id = data.id;
              }
              this.isSaved = true;
              this.$buefy.toast.open('Saved!');
              if (window.opener) {
                window.close();
              } else if (this.$router) {
                this.$router.go(-1);
              }
            });
          }
          else {
            response.json().then((data) => {
              alert('There was an error saving this Values set, please try again.');
            });
          }
        });
      },
      removeDeleted: function(screenSetId) {
          this.values = this.values.filter((itm) => {
              return itm.id != screenSetId;
          });
      },
      async deleteSet(setId) {
        try {
          await fetch(`/api/v1/screen_sets/${setId}/`, {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': this.$root.$data.csrfToken,
            }
          });
          this.removeDeleted(setId);
        } catch (error) {
          this.$buefy.toast.open({
            message: 'Failed to delete values set',
            type: 'is-danger'
          });
        }
      },
    },
    computed: {
      templates: function() {
        return this.values.filter((itm) => {
          return itm.is_template === true || itm.internal_identifier;
        });
      },
      userSets: function() {
        return this.values.filter((itm) => {
          return itm.is_template === false && !itm.internal_identifier;
        });
      },
      filteredSets() {
        if (!this.searchQuery) {
          return this.userSets;
        }

        const query = this.searchQuery.trim().toLowerCase();
        return this.userSets.filter(set => {
          switch (this.searchFilter) {
            case 'name':
              return set.name?.toLowerCase().includes(query);

            case 'all':
              return set.name?.toLowerCase().includes(query);
          }
        });
      },
    },
    created: async function() {
      this.$root.$data.csrfToken = document.querySelector('[name="csrfmiddlewaretoken"]').value;
      this.advisor = new Advisor();
      let metricResponse = await fetch('/api/v1/impact_factors/');
      let metricData = await metricResponse.json();
      metricData.metrics.forEach((itm) => {
        // TODO: This is copy pasta from PortfolioBuilderView.vue and should be replaced with a unified API provider
        itm.active = false;
        itm.visible = true;
        itm.userPercentile = 0;
        itm.switchState = false;
        itm.manuallySet = false;

        this.metrics.byId[itm.id] = itm;
        this.metrics.a.push(itm);
      });

      let ssResponse = await fetch('/api/v1/screen_sets/');
      let ssData = await ssResponse.json();
      for (let i in ssData.screen_sets) {
        let set = ssData.screen_sets[i];
        set.metric_ids = [];
        set.metrics.forEach((itm, idx, arr) => {
          set.metric_ids.push(itm.metric_description);
          itm.details = this.metrics.byId[itm.metric_description];
        });
        this.values.push(set);
      }
      
      let editableResponse = await fetch('/api/v1/screen_sets/?editable_only=True');
      let editableData = await editableResponse.json();
      this.editableSets = new Set(editableData.screen_sets.map((set) => set.id));

      this.loaded = true;
    },
    watch: {
      searchQuery: _.debounce(function (newQuery) {
      }, 300),
    },
  }
</script>

<style lang="scss" scoped>
.card {
  overflow: visible;
}
.screen-detail-pane.bottom.box {
  height: 40%;
}
.values-sets-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
}

.field.has-addons {
  max-width: 600px;
  margin: 2rem auto;
  
  .input {
    border-radius: 20px;
    padding-left: 2.5rem;
    height: 2.5rem;
    border: 1px solid #e1e1e1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
    
    &:focus {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      border-color: #b8b8b8;
    }
  }
  
  .icon.is-left {
    height: 2.5rem;
    width: 2.5rem;
  }
}

/* Add some spacing between cards */
.values-sets-container > * {
  margin-bottom: 1rem;
}
</style>
